import type { BugReportStatus, BugReportType, IBugReportQuery } from './types';

/** 25 MiB — mirrors MAX_BUG_REPORT_ATTACHMENT_SIZE_BYTES on the API. */
export const MAX_BUG_REPORT_ATTACHMENT_SIZE_BYTES = 26_214_400;
export const MAX_BUG_REPORT_ATTACHMENT_SIZE_MB = 25;
export const MAX_BUG_REPORT_ATTACHMENTS = 10;

/** Images plus the document types people actually paste as evidence. */
export const BUG_REPORT_ACCEPT = 'image/*,.pdf,.txt,.log,.csv,.json,.har';

/** An i18n key paired with its English fallback, for `t(key, fallback)`. */
interface Copy {
    readonly key: string;
    readonly text: string;
}

interface BugReportTypeMeta {
    readonly labelKey: string;
    readonly label: string;
    readonly descriptionKey: string;
    readonly description: string;
    /** Lucide icon name resolved in the picker. */
    readonly icon: 'bug' | 'lightbulb' | 'lifeBuoy';
    /**
     * Per-type form copy. A bug, an idea and a question are three different
     * conversations, and asking "what did you expect to happen?" of someone
     * suggesting a feature reads as a non-sequitur — so every field below the
     * picker re-labels itself when the type changes.
     */
    readonly titleLabel: Copy;
    readonly titlePlaceholder: Copy;
    readonly detailsLabel: Copy;
    readonly detailsPlaceholder: Copy;
    readonly attachmentLabel: Copy;
    readonly submitLabel: Copy;
}

/**
 * The three things a user can send us. Order matters — it is the order the
 * picker renders, most-common first.
 */
export const BUG_REPORT_TYPE_META: Record<BugReportType, BugReportTypeMeta> = {
    BUG: {
        labelKey: 'bugReports.type.bug',
        label: 'Bug Report',
        descriptionKey: 'bugReports.type.bugDescription',
        description: 'Something is broken',
        icon: 'bug',
        titleLabel: { key: 'bugReports.form.bug.title', text: 'What went wrong?' },
        titlePlaceholder: {
            key: 'bugReports.form.bug.titlePlaceholder',
            text: 'e.g. Campaign list fails to load',
        },
        detailsLabel: { key: 'bugReports.form.bug.details', text: 'Describe the problem' },
        detailsPlaceholder: {
            key: 'bugReports.form.bug.detailsPlaceholder',
            text: 'What did you expect to happen, and what happened instead?',
        },
        attachmentLabel: {
            key: 'bugReports.form.bug.attachment',
            text: 'Attach a screenshot as proof',
        },
        submitLabel: { key: 'bugReports.form.bug.submit', text: 'Send report' },
    },
    FEATURE: {
        labelKey: 'bugReports.type.feature',
        label: 'Feature Improvement',
        descriptionKey: 'bugReports.type.featureDescription',
        description: 'Suggest an improvement',
        icon: 'lightbulb',
        titleLabel: {
            key: 'bugReports.form.feature.title',
            text: 'What would you like to see?',
        },
        titlePlaceholder: {
            key: 'bugReports.form.feature.titlePlaceholder',
            text: 'e.g. Schedule campaigns to repeat weekly',
        },
        detailsLabel: { key: 'bugReports.form.feature.details', text: 'Describe your idea' },
        detailsPlaceholder: {
            key: 'bugReports.form.feature.detailsPlaceholder',
            text: 'What are you trying to do, and how would this help?',
        },
        attachmentLabel: {
            key: 'bugReports.form.feature.attachment',
            text: 'Attach a mockup or example',
        },
        submitLabel: { key: 'bugReports.form.feature.submit', text: 'Send idea' },
    },
    SUPPORT: {
        labelKey: 'bugReports.type.support',
        label: 'Support Request',
        descriptionKey: 'bugReports.type.supportDescription',
        description: 'Get help from our team',
        icon: 'lifeBuoy',
        titleLabel: {
            key: 'bugReports.form.support.title',
            text: 'What do you need help with?',
        },
        titlePlaceholder: {
            key: 'bugReports.form.support.titlePlaceholder',
            text: 'e.g. How do I import contacts from a CSV?',
        },
        detailsLabel: { key: 'bugReports.form.support.details', text: 'Tell us more' },
        detailsPlaceholder: {
            key: 'bugReports.form.support.detailsPlaceholder',
            text: 'What have you tried so far, and where did you get stuck?',
        },
        attachmentLabel: {
            key: 'bugReports.form.support.attachment',
            text: 'Attach a screenshot',
        },
        submitLabel: { key: 'bugReports.form.support.submit', text: 'Send request' },
    },
};

/**
 * Reporter-facing status copy. The badge variants deliberately differ from the
 * super admin portal's: a reporter cares whether their report is still open,
 * being worked, or settled — not where it sits in someone else's triage queue.
 */
export const BUG_REPORT_STATUS_META: Record<
    BugReportStatus,
    {
        readonly labelKey: string;
        readonly label: string;
        readonly variant: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info';
    }
> = {
    NEW: { labelKey: 'bugReports.status.new', label: 'New', variant: 'info' },
    IN_PROGRESS: {
        labelKey: 'bugReports.status.inProgress',
        label: 'In Progress',
        variant: 'warning',
    },
    REOPENED: { labelKey: 'bugReports.status.reopened', label: 'Reopened', variant: 'warning' },
    PLANNED: { labelKey: 'bugReports.status.planned', label: 'Planned', variant: 'primary' },
    FUTURE: { labelKey: 'bugReports.status.future', label: 'Future', variant: 'default' },
    DONE: { labelKey: 'bugReports.status.done', label: 'Done', variant: 'success' },
    DECLINED: { labelKey: 'bugReports.status.declined', label: 'Declined', variant: 'danger' },
};

/** Mirrors CreateBugReportCommentDto on the API — block it here, not at 400. */
export const MAX_BUG_REPORT_COMMENT_LENGTH = 10_000;

/**
 * Merge tags the thread composer offers.
 *
 * Scoped to what a support thread can actually resolve — the report being
 * replied to and the person replying. There is no contact or campaign context
 * here, so the broader catalogue the campaign composer offers would be a menu
 * of tokens that resolve to nothing. Every token listed here has a value in
 * `buildReportVariableValues`, and the composer substitutes them on send.
 */
export const BUG_REPORT_VARIABLE_GROUPS = [
    {
        label: 'Report',
        items: [
            { token: 'report_title', label: 'Report title' },
            { token: 'report_type', label: 'Report type' },
            { token: 'report_status', label: 'Current status' },
            { token: 'report_id', label: 'Report ID' },
            { token: 'page_url', label: 'Page it was filed from' },
        ],
    },
    {
        label: 'You',
        items: [
            { token: 'my_name', label: 'Your name' },
            { token: 'my_email', label: 'Your email' },
            { token: 'organization_name', label: 'Your organization' },
        ],
    },
] as const;

/** The reporter's own list is short-lived and read in one sitting. */
export const MY_BUG_REPORTS_PAGE_SIZE = 20;

/**
 * Search param the API's bug-report notifications put in their `actionUrl`
 * (see `NotificationTriggerWorker.onBugReportCommentCreated`). Read by
 * ReportBugTrigger to open the dialog on that thread. Changing it here means
 * changing it there — nothing links the two but this comment.
 */
export const BUG_REPORT_DEEP_LINK_PARAM = 'bugReportId';

export const BUG_REPORTS_KEYS = {
    all: ['bug-reports'] as const,
    lists: () => ['bug-reports', 'list'] as const,
    list: (params: IBugReportQuery) => ['bug-reports', 'list', params] as const,
    detail: (id: string) => ['bug-reports', 'detail', id] as const,
};
